import { Auth0ContextInterface, initialContext, useAuth0 } from '@auth0/auth0-react';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { onboardingQuery } from '@/features/user/queries';
import {
  auth0Exports,
  authUser,
  isReauthenticationError,
  redirectToSessionEnded,
} from '@/features/auth/utils';

export const devBypassAuth0: Auth0ContextInterface = {
  ...initialContext,
  isLoading: false,
  isAuthenticated: true,
  getAccessTokenSilently: (async () => '') as Auth0ContextInterface['getAccessTokenSilently'],
  loginWithRedirect: async () => undefined,
  logout: async () => undefined,
};

export function useAuth() {
  const auth0 = useAuth0();

  const {
    data: user = {
      id: '',
      firstName: '',
      lastName: '',
      email: '',
      intercomUserHash: undefined,
      organizationId: '',
      organizationName: '',
      organizations: [],
      defaultTranslationLanguage: 'en-US',
      systemLanguage: 'en-US',
      projectRoleIds: undefined,
      organizationRole: undefined,
      lastLoginAt: undefined,
      previousLoginAt: undefined,
    },
  } = useQuery({
    ...onboardingQuery(),
    enabled: auth0.isAuthenticated,
    select: authUser,
  });

  return {
    isLoading: auth0.isLoading,
    isAuthenticated: auth0.isAuthenticated,
    login: (options?: {
      appState?: { returnTo?: string; organizationName?: string; activationSuccess?: boolean };
      screenHint?: 'signup';
      loginHint?: string;
      connection?: string;
      invitationToken?: string;
    }) =>
      auth0.loginWithRedirect({
        appState: options?.appState,
        authorizationParams: {
          audience: import.meta.env.VITE_AUTH0_AUDIENCE,
          connection: options?.connection ?? import.meta.env.VITE_AUTH0_CONNECTION,
          screen_hint: options?.screenHint,
          login_hint: options?.loginHint,
          'ext-invitation_token': options?.invitationToken,
        },
      }),
    logout: () =>
      auth0.logout({
        logoutParams: { returnTo: window.location.origin + '/login' },
      }),
    user,
  };
}

export function Auth0Exporter() {
  const { isAuthenticated, isLoading, getAccessTokenSilently, logout, error } = useAuth0();
  auth0Exports.setIsAuthenticated(isAuthenticated);
  auth0Exports.setGetAccessTokenSilently(getAccessTokenSilently);
  auth0Exports.setLogout((returnTo?: string) =>
    logout({ logoutParams: { returnTo: returnTo ?? window.location.origin + '/login' } }),
  );
  if (!isLoading) {
    auth0Exports.setReady();
  }

  useEffect(() => {
    if (error && isReauthenticationError(error)) {
      redirectToSessionEnded('sessionExpired');
    }
  }, [error]);

  return null;
}
